/**
 * VideoLibrary.js - Video upload and management component
 */

import React, { useState, useRef } from 'react';
import axiosInstance from '../../axiosInstance';

const VideoLibrary = ({ videos, onRefresh, loading }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      uploadVideo(file);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('video/')) {
      uploadVideo(file);
    } else {
      alert('Please drop a video file');
    }
  };

  const uploadVideo = async (file) => {
    try {
      setUploading(true);
      setUploadProgress(0);

      const formData = new FormData();
      formData.append('video', file);
      formData.append('name', file.name.replace(/\.[^/.]+$/, '')); // Remove extension
      formData.append('description', `Uploaded on ${new Date().toLocaleDateString()}`);

      const response = await axiosInstance.post('/video-sync/videos/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        },
      });

      console.log('[VideoLibrary] Upload response:', response.data);

      // Show success message
      setSuccessMessage('Video uploaded successfully! Encoding in progress...');

      // Refresh list
      setTimeout(() => onRefresh(), 2000);

      // Fade out message after 4 seconds
      setTimeout(() => setSuccessMessage(''), 4000);
    } catch (error) {
      console.error('[VideoLibrary] Upload error:', error);
      alert('Upload failed: ' + (error.response?.data?.error || error.message));
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const deleteVideo = async (videoId) => {
    if (!window.confirm('Are you sure you want to delete this video?')) return;

    try {
      await axiosInstance.delete(`/video-sync/videos/${videoId}`);
      alert('Video deleted successfully');
      onRefresh();
    } catch (error) {
      console.error('[VideoLibrary] Delete error:', error);
      alert('Delete failed: ' + (error.response?.data?.error || error.message));
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      uploading: { color: '#3498db', text: 'Uploading' },
      encoding: { color: '#f39c12', text: 'Encoding' },
      ready: { color: '#27ae60', text: 'Ready' },
      error: { color: '#e74c3c', text: 'Error' },
    };

    const badge = badges[status] || badges.error;

    return (
      <span
        style={{
          backgroundColor: badge.color,
          color: 'white',
          padding: '4px 8px',
          borderRadius: '4px',
          fontSize: '12px',
          fontWeight: 'bold',
        }}
      >
        {badge.text}
      </span>
    );
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div className="video-library">
      {/* Upload Section */}
      <div className="upload-section">
        {/* Success Message */}
        {successMessage && (
          <div className="success-message-fade">
            {successMessage}
          </div>
        )}

        <div
          className={`drop-zone ${dragOver ? 'drag-over' : ''}`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="video/*"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />

          {uploading ? (
            <div className="upload-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${uploadProgress}%` }}></div>
              </div>
              <p>Uploading... {uploadProgress}%</p>
            </div>
          ) : (
            <>
              <div className="upload-icon">📁</div>
              <h3>Drop video file here or click to browse</h3>
              <p>Supports MP4, MOV, AVI, MKV, WebM</p>
              <p className="upload-note">Videos will be automatically encoded to 1920x1080 @ 30fps for perfect sync</p>
            </>
          )}
        </div>
      </div>

      {/* Video Grid */}
      <div className="video-grid">
        {loading ? (
          <div className="loading">Loading videos...</div>
        ) : videos.length === 0 ? (
          <div className="empty-state">
            <p>No videos uploaded yet. Upload your first video above!</p>
          </div>
        ) : (
          videos.map((video) => (
            <div
              key={video._id}
              className={`video-card ${selectedVideo?._id === video._id ? 'selected' : ''}`}
              onClick={() => setSelectedVideo(video)}
            >
              {/* Thumbnail */}
              <div className="video-thumbnail">
                {video.thumbnailPath ? (
                  <img
                    src={`/api/video-sync/videos/${video._id}/thumbnail`}
                    alt={video.name}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <div className="thumbnail-placeholder">🎬</div>
                )}
                <div className="video-duration">{formatDuration(video.duration)}</div>
              </div>

              {/* Info */}
              <div className="video-info">
                <h4>{video.name}</h4>
                <p className="video-meta">
                  {video.resolution.width}x{video.resolution.height} • {video.framerate}fps
                </p>
                <p className="video-meta">
                  {formatFileSize(video.encodedSize)} • {video.totalFrames} frames
                </p>
                <div className="video-status">
                  {getStatusBadge(video.status)}
                </div>
              </div>

              {/* Actions */}
              <div className="video-actions">
                <button
                  className="btn-icon"
                  onClick={(e) => { e.stopPropagation(); deleteVideo(video._id); }}
                  title="Delete video"
                >
                  🗑️
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Video Details Panel */}
      {selectedVideo && (
        <div className="video-details-panel">
          <h3>Video Details</h3>
          <button className="close-btn" onClick={() => setSelectedVideo(null)}>✕</button>

          <div className="details-grid">
            <div className="detail-row">
              <strong>Name:</strong>
              <span>{selectedVideo.name}</span>
            </div>
            <div className="detail-row">
              <strong>Status:</strong>
              {getStatusBadge(selectedVideo.status)}
            </div>
            <div className="detail-row">
              <strong>Duration:</strong>
              <span>{formatDuration(selectedVideo.duration)}</span>
            </div>
            <div className="detail-row">
              <strong>Resolution:</strong>
              <span>{selectedVideo.resolution.width}x{selectedVideo.resolution.height}</span>
            </div>
            <div className="detail-row">
              <strong>Framerate:</strong>
              <span>{selectedVideo.framerate} fps</span>
            </div>
            <div className="detail-row">
              <strong>Total Frames:</strong>
              <span>{selectedVideo.totalFrames.toLocaleString()}</span>
            </div>
            <div className="detail-row">
              <strong>File Size:</strong>
              <span>{formatFileSize(selectedVideo.encodedSize)}</span>
            </div>
            <div className="detail-row">
              <strong>Codec:</strong>
              <span>{selectedVideo.codec.toUpperCase()}</span>
            </div>
            <div className="detail-row">
              <strong>Times Deployed:</strong>
              <span>{selectedVideo.timesDeployed}</span>
            </div>
            <div className="detail-row">
              <strong>Uploaded:</strong>
              <span>{new Date(selectedVideo.createdAt).toLocaleString()}</span>
            </div>
          </div>

          {selectedVideo.errorMessage && (
            <div className="error-message">
              <strong>Error:</strong> {selectedVideo.errorMessage}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default VideoLibrary;
